import { Subscription } from '@rails/actioncable';
import { getCableConsumer } from './CableClient';
import type {
  FriendChannelEvent,
  FriendRequestReceivedEvent,
  FriendRequestAcceptedEvent,
  FriendRequestDeclinedEvent,
  FriendRemovedEvent,
  GameInvitationReceivedEvent,
  GameInvitationAcceptedEvent,
  GameInvitationDeclinedEvent,
  GameInvitationCancelledEvent,
  PresenceChangedEvent,
  MessageReceivedEvent,
  AchievementUnlockedEvent,
} from '../types/social';

type MessageCallback<T> = (data: T) => void;

/**
 * FriendChannel manages the ActionCable subscription for the logged-in user's
 * real-time social events.
 *
 * The backend streams to the user's personal channel:
 *   FriendChannel.broadcast_to(user, payload)
 *
 * This channel is user-scoped, receive-only, and stateless.
 * Subscribe once on login; unsubscribe on logout.
 * No game or session state is involved.
 *
 * Usage (via singleton):
 *   friendChannel
 *     .onFriendRequestReceived(handler)
 *     .onGameInvitationReceived(handler);
 *   await friendChannel.subscribe();
 *   // later:
 *   friendChannel.unsubscribe();
 */
export class FriendChannel {
  private subscription: Subscription | null = null;
  private _everConnected = false;

  // Per-event callbacks
  private _onFriendRequestReceived:    MessageCallback<FriendRequestReceivedEvent>    | null = null;
  private _onFriendRequestAccepted:    MessageCallback<FriendRequestAcceptedEvent>    | null = null;
  private _onFriendRequestDeclined:    MessageCallback<FriendRequestDeclinedEvent>    | null = null;
  private _onFriendRemoved:            MessageCallback<FriendRemovedEvent>            | null = null;
  private _onGameInvitationReceived:   MessageCallback<GameInvitationReceivedEvent>   | null = null;
  private _onGameInvitationAccepted:   MessageCallback<GameInvitationAcceptedEvent>   | null = null;
  private _onGameInvitationDeclined:   MessageCallback<GameInvitationDeclinedEvent>   | null = null;
  private _onGameInvitationCancelled:  MessageCallback<GameInvitationCancelledEvent>  | null = null;
  private _onPresenceChanged:           MessageCallback<PresenceChangedEvent>           | null = null;
  private _onMessageReceived:           MessageCallback<MessageReceivedEvent>           | null = null;
  private _onAchievementUnlocked:       MessageCallback<AchievementUnlockedEvent>       | null = null;
  private _onReconnect:  (() => void) | null = null;
  private _onDisconnect: (() => void) | null = null;
  private _onRejected:   (() => void) | null = null;

  // ─── Lifecycle ───────────────────────────────────────────

  /**
   * Subscribe to the user-scoped FriendChannel.
   * Safe to call multiple times — returns early if already subscribed.
   */
  async subscribe(): Promise<void> {
    if (this.subscription) {
      console.log('[FriendChannel] Already subscribed — skipping duplicate subscribe()');
      return;
    }

    console.log('[FriendChannel] subscribe()');
    const consumer = await getCableConsumer();

    this.subscription = consumer.subscriptions.create(
      { channel: 'FriendChannel' },
      {
        received: (data: unknown) => {
          const event = data as FriendChannelEvent;
          console.log(`[FriendChannel] received type=${event.type}`);
          this.handleMessage(event);
        },
        connected: () => {
          if (this._everConnected) {
            console.log('[FriendChannel] reconnected');
            this._onReconnect?.();
          } else {
            console.log('[FriendChannel] connected');
            this._everConnected = true;
          }
        },
        disconnected: () => {
          console.log('[FriendChannel] disconnected');
          this._onDisconnect?.();
        },
        rejected: () => {
          console.error('[FriendChannel] subscription REJECTED — check auth token');
          this._onRejected?.();
        },
      }
    );
  }

  /** Unsubscribe and clear all callbacks. */
  unsubscribe(): void {
    console.log(`[FriendChannel] unsubscribe()  hadSubscription=${this.subscription !== null}`);
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this._everConnected = false;
    this._onFriendRequestReceived   = null;
    this._onFriendRequestAccepted   = null;
    this._onFriendRequestDeclined   = null;
    this._onFriendRemoved           = null;
    this._onGameInvitationReceived  = null;
    this._onGameInvitationAccepted  = null;
    this._onGameInvitationDeclined  = null;
    this._onGameInvitationCancelled = null;
    this._onPresenceChanged         = null;
    this._onMessageReceived         = null;
    this._onAchievementUnlocked     = null;
    this._onReconnect               = null;
    this._onDisconnect              = null;
    this._onRejected                = null;
  }

  // ─── Message Dispatch ────────────────────────────────────

  private handleMessage(event: FriendChannelEvent): void {
    switch (event.type) {
      case 'friend_request_received':
        this._onFriendRequestReceived?.(event);
        break;
      case 'friend_request_accepted':
        this._onFriendRequestAccepted?.(event);
        break;
      case 'friend_request_declined':
        this._onFriendRequestDeclined?.(event);
        break;
      case 'friend_removed':
        this._onFriendRemoved?.(event);
        break;
      case 'game_invitation_received':
        this._onGameInvitationReceived?.(event);
        break;
      case 'game_invitation_accepted':
        this._onGameInvitationAccepted?.(event);
        break;
      case 'game_invitation_declined':
        this._onGameInvitationDeclined?.(event);
        break;
      case 'game_invitation_cancelled':
        this._onGameInvitationCancelled?.(event);
        break;
      case 'presence_changed':
        this._onPresenceChanged?.(event);
        break;
      case 'message_received':
        this._onMessageReceived?.(event);
        break;
      case 'achievement_unlocked':
        this._onAchievementUnlocked?.(event);
        break;
      default:
        console.warn('[FriendChannel] unknown event type:', (event as { type: string }).type);
    }
  }

  // ─── Register Callbacks ──────────────────────────────────

  onFriendRequestReceived(callback: MessageCallback<FriendRequestReceivedEvent>): this {
    this._onFriendRequestReceived = callback;
    return this;
  }

  onFriendRequestAccepted(callback: MessageCallback<FriendRequestAcceptedEvent>): this {
    this._onFriendRequestAccepted = callback;
    return this;
  }

  onFriendRequestDeclined(callback: MessageCallback<FriendRequestDeclinedEvent>): this {
    this._onFriendRequestDeclined = callback;
    return this;
  }

  onFriendRemoved(callback: MessageCallback<FriendRemovedEvent>): this {
    this._onFriendRemoved = callback;
    return this;
  }

  onGameInvitationReceived(callback: MessageCallback<GameInvitationReceivedEvent>): this {
    this._onGameInvitationReceived = callback;
    return this;
  }

  onGameInvitationAccepted(callback: MessageCallback<GameInvitationAcceptedEvent>): this {
    this._onGameInvitationAccepted = callback;
    return this;
  }

  onGameInvitationDeclined(callback: MessageCallback<GameInvitationDeclinedEvent>): this {
    this._onGameInvitationDeclined = callback;
    return this;
  }

  onGameInvitationCancelled(callback: MessageCallback<GameInvitationCancelledEvent>): this {
    this._onGameInvitationCancelled = callback;
    return this;
  }

  onPresenceChanged(callback: MessageCallback<PresenceChangedEvent>): this {
    this._onPresenceChanged = callback;
    return this;
  }

  onMessageReceived(callback: MessageCallback<MessageReceivedEvent>): this {
    this._onMessageReceived = callback;
    return this;
  }

  onAchievementUnlocked(callback: MessageCallback<AchievementUnlockedEvent>): this {
    this._onAchievementUnlocked = callback;
    return this;
  }

  /** Called every time the WebSocket reconnects after a drop. */
  onReconnect(callback: () => void): this {
    this._onReconnect = callback;
    return this;
  }

  /** Called whenever the WebSocket drops. */
  onDisconnect(callback: () => void): this {
    this._onDisconnect = callback;
    return this;
  }

  /**
   * Called when the server REJECTS the subscription. Social realtime may degrade
   * independently of gameplay; the consumer (FriendContext) uses this to mark the
   * channel disconnected rather than leaving the UI believing it is live.
   */
  onRejected(callback: () => void): this {
    this._onRejected = callback;
    return this;
  }
}

/** Singleton FriendChannel instance. Subscribe once on login, unsubscribe on logout. */
export const friendChannel = new FriendChannel();
